import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useAuth} from '@/contexts/AuthContext';
import {ReitService as reitService} from '@/services/investments';
import {CreateReitDepositDto} from "@/@types/reit.dto";

/**
 * Custom hook to handle the creation of a REIT (Real Estate Investment Trust) deposit.
 * This hook uses a mutation function to send deposit data via a service call and
 * invalidates related query caches upon the mutation's success.
 *
 * @return {Object} Returns the mutation object from the useMutation hook, which includes
 * mutation methods (mutate, mutateAsync) and state (isLoading, isError, isSuccess, etc.).
 */
export default function useCreateREITDeposit() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: CreateReitDepositDto) => {
      return await reitService.createDeposit(user!.id, input);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reit_deposits'] });
      qc.invalidateQueries({ queryKey: ['reit_holdings'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
    },
  });
}
